import { useSelector } from "react-redux";

import RadioButton from "@/components/ui/radio";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";

interface ScoringOptionsProps {
  setSelectedScoringOption: React.Dispatch<
    React.SetStateAction<ScoringOptions | null>
  >;
  selectedScoringOption: ScoringOptions | null;
}

export default function ScoringOptionsSection({
  setSelectedScoringOption,
  selectedScoringOption
}: ScoringOptionsProps) {
  const { visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );

  return (
    <div
      className={cn(
        "flex flex-col px-4 gap-1 pb-4",
        visualizationOrder.length ? "pt-4" : ""
      )}
    >
      <span className="text-sm font-semibold text-txtHeading">
        Scoring Method
      </span>
      <span className="text-[10px] text-txtBody">
        Select the score calculation method and formula to apply.
      </span>
      <div className="flex flex-col gap-2 mt-2">
        <div className="flex flex-col gap-1">
          <RadioButton
            name={`scoring-option-${ScoringOptionsEnum.NORMALIZED}`}
            label="Normalized Value"
            labelClassName="text-sm font-semibold text-txtPlaceholder"
            onCheck={() =>
              setSelectedScoringOption(ScoringOptionsEnum.NORMALIZED)
            }
            checked={selectedScoringOption === ScoringOptionsEnum.NORMALIZED}
          />
          <span className="ml-6 text-[10px] text-txtBody">
            Scores are adjusted by scaling original data to a standard range
            (0-100).
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <RadioButton
            name={`scoring-option-${ScoringOptionsEnum.SCALED}`}
            label="Scaled Value"
            labelClassName="text-sm font-semibold text-txtPlaceholder"
            onCheck={() => setSelectedScoringOption(ScoringOptionsEnum.SCALED)}
            checked={selectedScoringOption === ScoringOptionsEnum.SCALED}
          />
          <span className="ml-6 text-[10px] text-txtBody">
            Scores are calculated after applying a Log10 transformation to
            reduce extreme data variation.
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <RadioButton
            name={`scoring-option-${ScoringOptionsEnum.RAW}`}
            label="Raw Value"
            labelClassName="text-sm font-semibold text-txtPlaceholder"
            onCheck={() => setSelectedScoringOption(ScoringOptionsEnum.RAW)}
            checked={selectedScoringOption === ScoringOptionsEnum.RAW}
          />
          <span className="ml-6 text-[10px] text-txtBody">
            Scores are calculated directly using the original data values
            without modification.
          </span>
        </div>
      </div>
    </div>
  );
}
